/**
 * Gift Plate Section Component
 * 
 * Complete gift plate designer with:
 * - Editable text directly on plate
 * - Background options (solid colors + predefined images)
 * - Text & frame color selector
 * - Optional vertical date (always right side)
 * - Hole configuration
 * - Symbol picker panel
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import GiftPlatePreview from './GiftPlatePreview'
import SymbolPicker from './SymbolPicker'
import { useCartStore } from '@/store/cartStore'
import Button from '@/components/common/Button'
import toast from 'react-hot-toast'

const SOLID_COLORS = [
  { name: 'Weiß', value: '#FFFFFF' },
  { name: 'Schwarz', value: '#000000' },
  { name: 'Blau', value: '#0066CC' },
  { name: 'Rosa', value: '#FF69B4' },
]

// Predefined background images (using CSS gradients/patterns as placeholders)
// In production, these would be actual image assets
const BACKGROUND_IMAGES = [
  { id: 'grass', name: 'Gras / Rasen', value: 'linear-gradient(135deg, #7cb342 0%, #558b2f 100%)' },
  { id: 'carbon', name: 'Carbon Fiber', value: 'repeating-linear-gradient(45deg, #1a1a1a 0px, #1a1a1a 2px, #0d0d0d 2px, #0d0d0d 4px)' },
  { id: 'marble', name: 'Marmor', value: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 50%, #9e9e9e 100%)' },
  { id: 'brushed', name: 'Gebürstetes Metall', value: 'linear-gradient(90deg, #c0c0c0 0%, #a0a0a0 50%, #c0c0c0 100%)' },
  { id: 'dark-texture', name: 'Dunkle Textur', value: 'radial-gradient(circle, #2a2a2a 0%, #1a1a1a 100%)' },
  { id: 'light-texture', name: 'Helle Textur', value: 'radial-gradient(circle, #f5f5f5 0%, #e0e0e0 100%)' },
]

const TEXT_COLORS = [
  { name: 'Schwarz', value: '#000000' },
  { name: 'Weiß', value: '#FFFFFF' },
  { name: 'Rot', value: '#FF0000' },
  { name: 'Blau', value: '#0066CC' },
  { name: 'Grün', value: '#00AA00' },
  { name: 'Gold', value: '#FFD700' },
  { name: 'Silber', value: '#C0C0C0' },
]

const HOLE_POSITIONS = [
  { id: 'top-left', label: 'Oben Links' },
  { id: 'top-center', label: 'Oben Mitte' },
  { id: 'top-right', label: 'Oben Rechts' },
  { id: 'center-left', label: 'Mitte Links' },
  { id: 'center', label: 'Mitte' },
  { id: 'center-right', label: 'Mitte Rechts' },
  { id: 'bottom-left', label: 'Unten Links' },
  { id: 'bottom-center', label: 'Unten Mitte' },
  { id: 'bottom-right', label: 'Unten Rechts' },
]

const GiftPlateSection = () => {
  const { addItem } = useCartStore()

  // State
  const [text, setText] = useState('')
  const [backgroundType, setBackgroundType] = useState<'solid' | 'image'>('solid')
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF')
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null)
  const [textColor, setTextColor] = useState('#000000')
  const [showDate, setShowDate] = useState(false)
  const [date, setDate] = useState({ day: 1, month: 1, year: 2024 })
  const [datePosition, setDatePosition] = useState<'left' | 'right'>('right')
  const [holesEnabled, setHolesEnabled] = useState(false)
  const [holeCount, setHoleCount] = useState<1 | 2 | 4>(2)
  const [holePositions, setHolePositions] = useState<string[]>(['top-left', 'top-right'])

  // Frame color automatically matches text color
  const frameColor = textColor

  // Handle text change - convert to uppercase (letters only)
  const handleTextChange = (newText: string) => {
    if (newText.length <= 10) {
      // Convert letters to uppercase, preserve symbols/emojis
      const uppercaseText = newText.split('').map(char => {
        if (/[a-zA-Z]/.test(char)) {
          return char.toUpperCase()
        }
        return char
      }).join('')
      setText(uppercaseText)
    }
  }

  // Handle symbol insertion
  const handleSymbolClick = (symbol: string) => {
    if (text.length + symbol.length <= 10) {
      setText(text + symbol)
    } else {
      toast.error('Maximale Zeichenanzahl erreicht (10)')
    }
  }

  // Handle background image selection
  const handleBackgroundImageSelect = (image: typeof BACKGROUND_IMAGES[0]) => {
    setBackgroundImage(image.value)
    setBackgroundType('image')
  }

  // Handle hole position toggle
  const toggleHolePosition = (positionId: string) => {
    if (!holesEnabled) return

    setHolePositions((prev) => {
      if (prev.includes(positionId)) {
        return prev.filter((p) => p !== positionId)
      }
      // Enforce hole count limit
      if (prev.length >= holeCount) {
        return prev.slice(1).concat(positionId)
      }
      return [...prev, positionId]
    })
  }

  // Update hole positions when count changes
  const handleHoleCountChange = (count: 1 | 2 | 4) => {
    setHoleCount(count)
    if (count === 1) {
      setHolePositions(holePositions.slice(0, 1))
    } else if (count === 2) {
      setHolePositions(holePositions.slice(0, 2))
    } else if (count === 4) {
      // Default to corners
      if (holePositions.length < 4) {
        setHolePositions(['top-left', 'top-right', 'bottom-left', 'bottom-right'])
      }
    }
  }

  // Add to cart
  const handleAddToCart = () => {
    if (!text.trim()) {
      toast.error('Bitte geben Sie einen Text ein')
      return
    }

    addItem({
      productId: `gift-plate-${Date.now()}`,
      productName: `Geschenkkennzeichen - ${text}`,
      price: 19.99,
      quantity: 1,
      customization: {
        type: 'gift-plate',
        text,
        backgroundColor,
        backgroundImage,
        backgroundType,
        textColor,
        frameColor,
        showDate,
        date: showDate ? date : undefined,
        datePosition,
        holesEnabled,
        holeCount,
        holePositions,
      },
    })

    toast.success('Geschenkkennzeichen zum Warenkorb hinzugefügt')
  }

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-dark-900 rounded-2xl p-6 sm:p-8 border border-gray-800"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <motion.div
              whileHover={{ rotate: 360, scale: 1.1 }}
              transition={{ duration: 0.5 }}
              className="p-4 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl text-white shadow-lg shadow-pink-500/50 text-4xl"
            >
              🎁
            </motion.div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gradient mb-3">
            Geschenkkennzeichen
          </h2>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Erstellen Sie ein personalisiertes Geschenkkennzeichen mit Ihrem eigenen Text, Emojis und Design
          </p>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Top Row: Configuration and Symbols */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Left Column: Configuration */}
            <div className="space-y-4 sm:space-y-6">
            {/* Step 1: Background Options */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Schritt 1: Hintergrund
              </label>
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => setBackgroundType('solid')}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    backgroundType === 'solid'
                      ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                      : 'border-gray-700 bg-dark-800/50 text-gray-400'
                  }`}
                >
                  Farbe
                </button>
                <button
                  onClick={() => setBackgroundType('image')}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    backgroundType === 'image'
                      ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                      : 'border-gray-700 bg-dark-800/50 text-gray-400'
                  }`}
                >
                  Bild
                </button>
              </div>

              {backgroundType === 'solid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  {SOLID_COLORS.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => {
                        setBackgroundColor(color.value)
                        setBackgroundImage(null)
                      }}
                      className={`h-12 rounded-lg border-2 transition-all ${
                        backgroundColor === color.value && !backgroundImage
                          ? 'border-primary-500 ring-2 ring-primary-500/50'
                          : 'border-gray-700'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {BACKGROUND_IMAGES.map((image) => (
                    <button
                      key={image.id}
                      onClick={() => handleBackgroundImageSelect(image)}
                      className={`h-16 rounded-lg border-2 transition-all relative overflow-hidden ${
                        backgroundImage === image.value
                          ? 'border-primary-500 ring-2 ring-primary-500/50'
                          : 'border-gray-700'
                      }`}
                      style={{ background: image.value }}
                      title={image.name}
                    >
                      <span className="absolute bottom-1 left-1 right-1 text-xs text-white font-bold bg-black/50 px-1 rounded">
                        {image.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Step 2: Text & Frame Color */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Schritt 2: Text- & Rahmenfarbe
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                {TEXT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setTextColor(color.value)}
                    className={`h-12 rounded-lg border-2 transition-all flex items-center justify-center ${
                      textColor === color.value
                        ? 'border-primary-500 ring-2 ring-primary-500/50'
                        : 'border-gray-700'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {color.value === '#FFFFFF' && (
                      <div className="w-6 h-6 border-2 border-gray-400 rounded" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Rahmenfarbe passt sich automatisch an
              </p>
            </div>

            {/* Step 3: Vertical Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Schritt 3: Datum (optional)
              </label>
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showDate}
                    onChange={(e) => setShowDate(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-gray-400">Datum anzeigen</span>
                </label>
              </div>

              {showDate && (
                <div className="mb-4">
                  <label className="block text-xs text-gray-400 mb-2">Datum Position</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setDatePosition('left')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        datePosition === 'left'
                          ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                          : 'border-gray-700 bg-dark-800/50 text-gray-400'
                      }`}
                    >
                      Links
                    </button>
                    <button
                      onClick={() => setDatePosition('right')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        datePosition === 'right'
                          ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                          : 'border-gray-700 bg-dark-800/50 text-gray-400'
                      }`}
                    >
                      Rechts
                    </button>
                  </div>
                </div>
              )}

              {showDate && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Tag</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={date.day}
                      onChange={(e) =>
                        setDate({ ...date, day: parseInt(e.target.value) || 1 })
                      }
                      className="w-full px-3 py-2 bg-dark-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Monat</label>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={date.month}
                      onChange={(e) =>
                        setDate({ ...date, month: parseInt(e.target.value) || 1 })
                      }
                      className="w-full px-3 py-2 bg-dark-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Jahr</label>
                    <input
                      type="number"
                      min="1900"
                      max="2100"
                      value={date.year}
                      onChange={(e) =>
                        setDate({ ...date, year: parseInt(e.target.value) || 2024 })
                      }
                      className="w-full px-3 py-2 bg-dark-800 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Step 4: Hole Configuration */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Schritt 4: Löcher (optional)
              </label>
              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={holesEnabled}
                    onChange={(e) => {
                      setHolesEnabled(e.target.checked)
                      if (!e.target.checked) {
                        setHolePositions([])
                      } else {
                        // Default positions
                        if (holeCount === 1) {
                          setHolePositions(['top-center'])
                        } else if (holeCount === 2) {
                          setHolePositions(['top-left', 'top-right'])
                        } else {
                          setHolePositions(['top-left', 'top-right', 'bottom-left', 'bottom-right'])
                        }
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="text-gray-400">Löcher aktivieren</span>
                </label>

                {holesEnabled && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Anzahl</label>
                      <div className="flex gap-3">
                        {[1, 2, 4].map((count) => (
                          <button
                            key={count}
                            onClick={() => handleHoleCountChange(count as 1 | 2 | 4)}
                            className={`px-4 py-2 rounded-lg border-2 transition-all ${
                              holeCount === count
                                ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                                : 'border-gray-700 bg-dark-800/50 text-gray-400'
                            }`}
                          >
                            {count}
                          </button>
                        ))}
                      </div>
                    </div>

                      <div>
                      <label className="block text-xs text-gray-400 mb-2">Positionen</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {HOLE_POSITIONS.map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => toggleHolePosition(pos.id)}
                            disabled={!holesEnabled}
                            className={`px-3 py-2 text-xs rounded-lg border-2 transition-all ${
                              holePositions.includes(pos.id)
                                ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                                : 'border-gray-700 bg-dark-800/50 text-gray-400 opacity-50'
                            }`}
                          >
                            {pos.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Add to Cart */}
            <div className="pt-4">
              <Button
                onClick={handleAddToCart}
                disabled={!text.trim()}
                size="lg"
                className="w-full"
              >
                In den Warenkorb
              </Button>
            </div>
            </div>

            {/* Right Column: Symbol Picker */}
            <div>
              <SymbolPicker onSymbolClick={handleSymbolClick} />
              <div className="mt-4 p-4 bg-dark-800 rounded-xl border border-gray-700">
                <p className="text-xs text-gray-400">
                  <strong>Tipp:</strong> Klicken Sie auf ein Symbol, um es in den Text einzufügen. 
                  Klicken Sie auf den Text in der Vorschau, um ihn direkt zu bearbeiten.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Aktuell: {text.length}/10 Zeichen
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Row: Preview - Full Width */}
          <div className="w-full">
            <GiftPlatePreview
              text={text}
              onTextChange={handleTextChange}
              backgroundColor={backgroundColor}
              backgroundImage={backgroundImage}
              textColor={textColor}
              frameColor={frameColor}
              showDate={showDate}
              date={showDate ? date : undefined}
              datePosition={datePosition}
              holesEnabled={holesEnabled}
              holePositions={holePositions}
            />
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default GiftPlateSection
